import dagre from 'dagre';

/**
 * Layout configurations for different visualization types
 */
export const LAYOUT_CONFIGS = {
  architecture: {
    direction: 'TB', // Top to bottom - better for system overview
    nodeSep: 40,     // Horizontal spacing between nodes
    rankSep: 60,     // Vertical spacing between ranks (rows)
    marginX: 20,
    marginY: 20,
  },
  callgraph: {
    direction: 'TB', // Callers at top, callees at bottom
    nodeSep: 40,
    rankSep: 80,
    marginX: 20,
    marginY: 20,
  },
  files: {
    direction: 'TB', // Top to bottom tree structure
    nodeSep: 30,
    rankSep: 60,
    marginX: 20,
    marginY: 20,
  }
};

/**
 * Node dimensions by type
 */
export const NODE_DIMENSIONS = {
  module: { width: 200, height: 70 },
  function: { width: 220, height: 80 },
  file: { width: 180, height: 60 },
  default: { width: 160, height: 55 },
};

/**
 * Get dimensions for a node type
 */
export function getNodeDimensions(nodeType) {
  return NODE_DIMENSIONS[nodeType] || NODE_DIMENSIONS.default;
}

/**
 * Get dimensions scaled by metrics (LOC)
 * Larger directories get visually larger nodes
 */
export function getMetricBasedDimensions(nodeType, metrics) {
  const base = NODE_DIMENSIONS[nodeType] || NODE_DIMENSIONS.default;
  if (!metrics?.loc) return base;

  // Scale: 0-500 LOC = base, 500-2000 = +20%, 2000+ = +40%
  let scale = 1;
  if (metrics.loc >= 2000) {
    scale = 1.4;
  } else if (metrics.loc >= 500) {
    scale = 1.2;
  }

  return {
    width: Math.round(base.width * scale),
    height: Math.round(base.height * scale)
  };
}

/**
 * Get color based on complexity level
 * Low = green, Medium = yellow, High = orange, Critical = red
 */
export function getComplexityColor(avgComplexity) {
  if (!avgComplexity || avgComplexity < 5) return '#22c55e';   // green - low
  if (avgComplexity < 10) return '#eab308';  // yellow - medium
  if (avgComplexity < 20) return '#f97316';  // orange - high
  return '#ef4444';                           // red - critical
}

/**
 * Get complexity level class name for CSS styling
 */
export function getComplexityLevel(avgComplexity) {
  if (!avgComplexity || avgComplexity < 5) return 'complexity-low';
  if (avgComplexity < 10) return 'complexity-medium';
  if (avgComplexity < 20) return 'complexity-high';
  return 'complexity-critical';
}

/**
 * Get churn level class name for CSS styling
 * Based on commits in last 30 days
 */
export function getChurnLevel(churn) {
  if (!churn || churn < 3) return 'churn-low';
  if (churn < 8) return 'churn-medium';
  if (churn < 15) return 'churn-high';
  return 'churn-critical';
}

/**
 * Get LOC level class name for CSS styling
 */
export function getLocLevel(loc) {
  if (!loc || loc < 500) return 'loc-low';
  if (loc < 1500) return 'loc-medium';
  if (loc < 5000) return 'loc-high';
  return 'loc-critical';
}

/**
 * Get color class based on the selected color mode and metrics
 */
export function getColorClass(colorMode, metrics) {
  if (!metrics) return '';

  switch (colorMode) {
    case 'complexity':
      return getComplexityLevel(metrics.avgComplexity);
    case 'churn':
      return getChurnLevel(metrics.churn);
    case 'loc':
      return getLocLevel(metrics.loc);
    case 'type':
    default:
      return ''; // No color class for type mode (use default styling)
  }
}

/**
 * Apply dagre layout to nodes and edges
 */
export function applyDagreLayout(nodes, edges, vizType = 'architecture') {
  if (nodes.length === 0) return { nodes: [], edges };

  const config = LAYOUT_CONFIGS[vizType] || LAYOUT_CONFIGS.architecture;
  const useMetricSizing = vizType === 'architecture';

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: config.direction,
    nodesep: config.nodeSep,
    ranksep: config.rankSep,
    marginx: config.marginX,
    marginy: config.marginY,
  });

  // Add nodes with dimensions (use metric-based sizing for architecture view)
  nodes.forEach((node) => {
    const { width, height } = useMetricSizing && node.data?.metrics
      ? getMetricBasedDimensions(node.type, node.data.metrics)
      : getNodeDimensions(node.type);
    g.setNode(node.id, { width, height });
  });

  // Add edges
  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  // Run layout
  dagre.layout(g);

  // Apply calculated positions
  const layoutedNodes = nodes.map((node) => {
    const dagreNode = g.node(node.id);
    if (!dagreNode) return node;

    const { width, height } = useMetricSizing && node.data?.metrics
      ? getMetricBasedDimensions(node.type, node.data.metrics)
      : getNodeDimensions(node.type);

    return {
      ...node,
      position: {
        x: dagreNode.x - width / 2,
        y: dagreNode.y - height / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

/**
 * Get node color by type
 */
export function getNodeColor(nodeType, kind) {
  switch (nodeType) {
    case 'module':
      return '#8b5cf6'; // Purple
    case 'function':
      if (kind === 'class') return '#f59e0b'; // Orange for classes
      if (kind === 'method') return '#22c55e'; // Green for methods
      return '#3b82f6'; // Blue for functions
    case 'file':
      return '#64748b'; // Slate
    default:
      return '#6b7280'; // Gray
  }
}
